import type { FieldValueDiff } from '@mimir/common/dto/mdfModels';
import type { MetadataState } from './itemMetadataUpdater';

/**
 * Error info attached to metadata conflict errors thrown during upload.
 */
export interface MetadataConflictErrorInfo {
  itemId: string;
  conflicts: FieldValueDiff[];
  conflictMetadata: MetadataState;
}

/**
 * Error info attached to metadata form type conflict errors thrown during upload.
 */
export interface MetadataFormTypeConflictErrorInfo {
  itemId: string;
  conflictMetadata: MetadataState;
}

/**
 * Error thrown when metadata field values conflict during upload.
 */
export interface MetadataConflictError extends Error {
  name: 'MetadataConflictError';
  info: MetadataConflictErrorInfo;
}

/**
 * Error thrown when the metadata form type changed on server during upload.
 */
export interface MetadataFormTypeConflictError extends Error {
  name: 'MetadataFormTypeConflictError';
  info: MetadataFormTypeConflictErrorInfo;
}

export function isMetadataConflictError(err: unknown): err is MetadataConflictError {
  return (
    err !== null &&
    typeof err === 'object' &&
    'name' in err &&
    err.name === 'MetadataConflictError'
  );
}

export function isMetadataFormTypeConflictError(err: unknown): err is MetadataFormTypeConflictError {
  return (
    err !== null &&
    typeof err === 'object' &&
    'name' in err &&
    err.name === 'MetadataFormTypeConflictError'
  );
}

export function createMetadataConflictError(info: MetadataConflictErrorInfo): MetadataConflictError {
  const error = new Error(`Metadata conflicts detected for item ${info.itemId}`) as MetadataConflictError;
  error.name = 'MetadataConflictError';
  error.info = info;
  return error;
}

export function createMetadataFormTypeConflictError(info: MetadataFormTypeConflictErrorInfo): MetadataFormTypeConflictError {
  const error = new Error('Metadata form type conflict detected') as MetadataFormTypeConflictError;
  error.name = 'MetadataFormTypeConflictError';
  error.info = info;
  return error;
}
